import React, { useEffect, useState, ReactNode, useRef, useMemo } from "react";
import { clsx } from "clsx";
import Typography from "design-system/src/Foundation/Typography/Typography";
import Link from "design-system/src/Core/Link/Link";
import RisingSunIcon from "@src/components/general/RisingSunIcon/RisingSunIcon";
import useResponsive from "@src/hooks/useResponsive";
import Icon from "design-system/src/Foundation/Icon/Icon";
import { navigate } from "gatsby";

export interface IStandardBannerMessage {
  id: string;
  text: string;
  link?: string;
  icon?: string;
  backgroundColor?: string;
}
export interface IStandardBannerProps {
  onToggleTopBanner?: (visible: boolean) => void;
  handleClose?: () => void;
  message: IStandardBannerMessage;
}

const StandardBanner = (props: IStandardBannerProps) => {
  const { onToggleTopBanner, handleClose, message } = props;
  const { id, text, link, icon, backgroundColor } = message;
  const { isMobile } = useResponsive();

  const textRef = useRef<HTMLParagraphElement>(null);
  const textContentRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [textWidth, setTextWidth] = useState(0);

  useEffect(() => {
    onToggleTopBanner && onToggleTopBanner(true);
  }, [onToggleTopBanner]);

  useEffect(() => {
    if (textRef.current && textContentRef.current) {
      const _element = textRef.current;
      const _contentElement = textContentRef.current;

      const observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const contentWidth = _contentElement.scrollWidth;
          const containerWidth = entry.contentRect.width;

          setTextWidth(contentWidth);

          if (contentWidth > containerWidth + 3) {
            setIsOverflowing(true);
          } else {
            setIsOverflowing(false);
          }
        }
      });

      setTimeout(() => {
        observer.observe(_element);
      }, 1000);

      return () => {
        if (_element) {
          observer.unobserve(_element);
        }
      };
    }
  }, [textRef, textContentRef]);

  // const handleClose = () => {
  //   currentMessage?.dismiss();
  // };

  const renderMessage: () => ReactNode = () => {
    return !!link ? (
      <Link url={link} label={text} variant="body2" />
    ) : (
      <span dangerouslySetInnerHTML={{ __html: text || "" }} />
    );
  };

  const animationDuration = textWidth > 0 ? Math.max(1, textWidth / 75) : 8;

  const renderDuplicatedContent = () => {
    const separator = (
      <span className="banner-text-separator">&nbsp;&nbsp;•&nbsp;&nbsp;</span>
    );
    return (
      <>
        {renderMessage()}
        {separator}
        {renderMessage()}
        {separator}
      </>
    );
  };

  const animationStyle = isOverflowing
    ? {
        animation: `scrollText ${animationDuration}s linear infinite`,
        display: "inline-block",
        whiteSpace: "nowrap",
        willChange: "transform",
      }
    : {};

  return (
    <div className="notifications__wrapper">
      <div
        className={clsx(
          "notifications banner",
          {
            "text-overflow": isOverflowing,
          },
          link && "message--clickable"
        )}
        data-id-message={id}
        style={{
          backgroundColor,
        }}
      >
        <div className="notifications banner__wrapper">
          {!!link && (
            <span
              style={{
                cursor: "pointer",
                position: "absolute",
                height: "100%",
                width: "100%",
                top: 0,
                left: 0,
              }}
              onClick={() => navigate(link)}
            />
          )}
          <p
            className="notifications banner__content"
            style={{
              height: isMobile ? "unset" : undefined,
              overflow: isOverflowing ? "hidden" : "visible",
            }}
            ref={textRef}
          >
            {icon && (
              <RisingSunIcon
                icon={icon}
                className="notifications banner__icon"
              />
            )}
            <span ref={textContentRef} style={animationStyle}>
              <Typography as="span" color="white" variant="body2">
                {isOverflowing ? renderDuplicatedContent() : renderMessage()}
              </Typography>
            </span>
          </p>
        </div>
        <span
          className="notifications banner__icon icon-cross"
          onClick={handleClose}
        >
          <Icon icon="cross_close" size={16} />
        </span>
      </div>
    </div>
  );
};

export default StandardBanner;
